import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Input, Select, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionPackingClient,
  BomClient,
  formatNumber,
  refusalMessage,
  type AssemblyVersionDTO,
  type AssemblyVersionPackingDTO,
} from "../../api/manufacturing";

/**
 * The packer's list of an assembly version (0068): which box each piece of the
 * semi-finished goes in. The semi-finished is used up whole at packing, so this
 * is not consumption, only where things go. A one-box version needs no list:
 * every piece goes in its box.
 */

/** A piece of the semi-finished, and where the version packs it. */
export interface PackedPiece {
  part_id: number;
  number: string;
  name: string;
  unit: string | null;
  /** How many the semi-finished has, per unit; 0 when it has none (a piece SIGM names that it does not make). */
  made: number;
  /** Box number → how many go in it. */
  boxes: Map<number, number>;
  placed: number;
}

export interface Packing {
  pieces: PackedPiece[];
  rows: AssemblyVersionPackingDTO[];
  /** Every piece in its only box, with no list: a one-box version. */
  implicit: boolean;
  loading: boolean;
}

export function usePacking(version: AssemblyVersionDTO | null, semiFinishedPartId: number | null, boxes: number): Packing {
  const { http } = useAuth();
  const made = useQuery({
    queryKey: ["manufacturing", "bom", "explode", semiFinishedPartId, 1],
    queryFn: () => new BomClient(http).explode(semiFinishedPartId!, 1),
    enabled: version != null && semiFinishedPartId != null,
    staleTime: 60_000,
  });
  const list = useQuery({
    queryKey: ["manufacturing", "assembly-version-packing", version?.id],
    queryFn: async () =>
      (await new AssemblyVersionPackingClient(http).find({ size: 1000, filters: { assembly_version_id: [String(version!.id)] } })).content,
    enabled: version != null,
  });

  return useMemo(() => {
    const rows = list.data ?? [];
    const implicit = boxes <= 1 && rows.length === 0;
    const pieces = new Map<number, PackedPiece>();
    for (const line of made.data ?? []) {
      if (line.level !== 1) continue;
      const piece = pieces.get(line.part_id);
      if (piece) piece.made += Number(line.quantity);
      else
        pieces.set(line.part_id, {
          part_id: line.part_id,
          number: line.part_number,
          name: line.part_name,
          unit: line.unit_code,
          made: Number(line.quantity),
          boxes: new Map(),
          placed: 0,
        });
    }
    if (implicit) {
      for (const piece of pieces.values()) {
        piece.boxes.set(1, piece.made);
        piece.placed = piece.made;
      }
    }
    for (const row of rows) {
      let piece = pieces.get(row.part_id);
      if (!piece) {
        piece = { part_id: row.part_id, number: row.part_number ?? "", name: row.part_name ?? "", unit: row.part_unit_code ?? null, made: 0, boxes: new Map(), placed: 0 };
        pieces.set(row.part_id, piece);
      }
      piece.boxes.set(row.box_number, (piece.boxes.get(row.box_number) ?? 0) + Number(row.quantity));
      piece.placed += Number(row.quantity);
    }
    return {
      pieces: [...pieces.values()].sort((a, b) => a.number.localeCompare(b.number)),
      rows,
      implicit,
      loading: made.isLoading || list.isLoading,
    };
  }, [made.data, list.data, boxes, made.isLoading, list.isLoading]);
}

/** A short line on the semi-finished and how the version packs it, with what is wrong. */
export function PackingSummary({
  packing,
  semiFinishedPartId,
  semiFinishedNumber,
  boxes,
}: {
  packing: Packing;
  semiFinishedPartId: number;
  semiFinishedNumber: string | null;
  boxes: number;
}) {
  const { t } = useTranslation();
  if (packing.loading) return null;
  const made = packing.pieces.filter((piece) => piece.made > 0);
  const unplaced = made.filter((piece) => piece.placed < piece.made).length;
  const over = made.filter((piece) => piece.placed > piece.made).length;
  const foreign = packing.pieces.filter((piece) => piece.made === 0).length;
  const perBox = Array.from({ length: boxes }, (_, index) => ({
    box: index + 1,
    count: packing.pieces.filter((piece) => (piece.boxes.get(index + 1) ?? 0) > 0).length,
  }));
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line bg-surface-sunk px-4 py-1.5 text-[12px] lg:px-6">
      <span className="text-ink-muted">{t("packing.summary.semi-finished", "Semi-finished")}</span>
      <Link to="/manufacturing/parts/$partId" params={{ partId: String(semiFinishedPartId) }} search={{ tab: "bom" }} className="font-medium text-accent underline-offset-2 hover:underline">
        {semiFinishedNumber}
      </Link>
      <span className="text-ink-muted">
        {t("packing.summary.pieces", "×1 · {{n}} pieces", { n: made.length })}
        {boxes > 1 && `: ${perBox.map((entry) => t("packing.summary.in-box", "{{count}} in box {{box}}", entry)).join(", ")}`}
      </span>
      {unplaced > 0 && (
        <Badge size="sm" variant="warning">
          {t("packing.summary.unplaced", "{{n}} in no box", { n: unplaced })}
        </Badge>
      )}
      {over > 0 && (
        <Badge size="sm" variant="error">
          {t("packing.summary.over", "{{n}} packed more than made", { n: over })}
        </Badge>
      )}
      {foreign > 0 && (
        <Badge size="sm" variant="error">
          {t("packing.summary.foreign", "{{n}} not in the semi-finished", { n: foreign })}
        </Badge>
      )}
    </div>
  );
}

/** The pieces of the semi-finished that go in one box: moved, changed, added or taken out from here. */
export function PackingPanel({
  version,
  packing,
  box,
  boxes,
  readOnly,
  productId,
}: {
  version: AssemblyVersionDTO;
  packing: Packing;
  box: number;
  boxes: number;
  readOnly: boolean;
  /** Where the packing list is edited (the product's Assembly tab, design §14); a link to it when read only. */
  productId?: number | null;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new AssemblyVersionPackingClient(http), [http]);
  const [adding, setAdding] = useState("");
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["manufacturing", "assembly-version-packing", version.id] });
  const fail = (failure: unknown) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));

  const rowsHere = packing.rows.filter((row) => row.box_number === box);
  const placeable = packing.pieces.filter((piece) => piece.made > piece.placed);

  const save = useMutation({
    mutationFn: async ({ row, quantity, toBox }: { row: AssemblyVersionPackingDTO; quantity: number; toBox: number }) => {
      // Moving onto a box that already holds the piece adds to it: one row per piece and box.
      const there = toBox === row.box_number ? null : packing.rows.find((other) => other.part_id === row.part_id && other.box_number === toBox);
      if (there) {
        await client.update({ ...there, quantity: Number(there.quantity) + quantity });
        await client.remove(row.id);
      } else {
        await client.update({ ...row, quantity, box_number: toBox });
      }
    },
    onSuccess: refresh,
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (row: AssemblyVersionPackingDTO) => client.remove(row.id),
    onSuccess: refresh,
    onError: fail,
  });
  const add = useMutation({
    mutationFn: async (piece: PackedPiece) => {
      const there = packing.rows.find((row) => row.part_id === piece.part_id && row.box_number === box);
      const rest = piece.made - piece.placed;
      if (there) await client.update({ ...there, quantity: Number(there.quantity) + rest });
      else await client.create({ id: 0, assembly_version_id: version.id, part_id: piece.part_id, box_number: box, quantity: rest, note: null });
    },
    onSuccess: async () => {
      setAdding("");
      await refresh();
    },
    onError: fail,
  });

  if (packing.loading) return null;
  if (packing.implicit) {
    return (
      <div className="shrink-0 border-t border-line px-4 py-2 text-[12px] text-ink-muted lg:px-6">
        {t("packing.one-box", "One box: every piece of the semi-finished goes in it ({{n}} pieces).", { n: packing.pieces.length })}
      </div>
    );
  }
  const boxOptions = Array.from({ length: boxes }, (_, index) => ({
    value: String(index + 1),
    label: t("bom.section.box", "Box {{n}}", { n: index + 1 }),
  }));

  return (
    <div className="flex max-h-[45%] shrink-0 flex-col border-t border-line">
      <div className="flex flex-wrap items-center gap-2 bg-surface px-4 py-1.5 lg:px-6">
        <h3 className="text-[12px] font-semibold">{t("packing.title", "Pieces of the semi-finished in box {{n}}", { n: box })}</h3>
        <span className="text-[11px] text-ink-faint">{t("packing.hint", "Where they go, not what is used up: the semi-finished is used up whole.")}</span>
        {readOnly && productId != null && (
          <Link to="/products/$productId" params={{ productId: String(productId) }} search={{ tab: "assembly" }}
            className="ml-auto text-[12px] text-accent underline-offset-2 hover:underline">
            {t("packing.edit-on-product", "Edit on the product")}
          </Link>
        )}
        {!readOnly && placeable.length > 0 && (
          <span className="ml-auto flex items-center gap-2">
            <Select
              aria-label={t("packing.add", "Put in this box")}
              className="h-7 w-64 py-0"
              value={adding}
              placeholder={t("packing.add.placeholder", "A piece in no box")}
              options={placeable.map((piece) => ({
                value: String(piece.part_id),
                label: `${piece.number} · ${piece.name} (${formatNumber(piece.made - piece.placed)})`,
              }))}
              onChange={(event) => setAdding(event.target.value)}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={adding === ""}
              loading={add.isPending}
              onClick={() => {
                const piece = placeable.find((one) => String(one.part_id) === adding);
                if (piece) add.mutate(piece);
              }}
            >
              {t("packing.add", "Put in this box")}
            </Button>
          </span>
        )}
      </div>
      <div className="min-h-0 overflow-auto">
        {rowsHere.length === 0 ? (
          <p className="px-4 py-2 text-[12px] text-ink-muted lg:px-6">{t("packing.empty", "No piece of the semi-finished goes in this box.")}</p>
        ) : (
          <table className="w-full text-[13px]">
            <tbody>
              {rowsHere.map((row) => {
                const piece = packing.pieces.find((one) => one.part_id === row.part_id);
                const foreign = piece != null && piece.made === 0;
                const over = piece != null && piece.made > 0 && piece.placed > piece.made;
                return (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-4 py-1 lg:pl-6">
                      <Link to="/manufacturing/parts/$partId" params={{ partId: String(row.part_id) }} className="font-medium text-accent underline-offset-2 hover:underline">
                        {row.part_number}
                      </Link>
                      <span className="ml-2 text-ink-muted">{row.part_name}</span>
                      {foreign && (
                        <Badge size="sm" variant="error" className="ml-2">
                          {t("packing.foreign", "Not in the semi-finished")}
                        </Badge>
                      )}
                      {over && (
                        <Badge size="sm" variant="error" className="ml-2">
                          {t("packing.over", "{{placed}} packed, {{made}} made", { placed: formatNumber(piece.placed), made: formatNumber(piece.made) })}
                        </Badge>
                      )}
                    </td>
                    <td className="w-32 px-2 py-1">
                      {readOnly ? (
                        <span className="tabular-nums">{formatNumber(row.quantity)}</span>
                      ) : (
                        <Input
                          key={`${String(row.id)}-${String(row.quantity)}`}
                          aria-label={t("packing.quantity", "How many in this box")}
                          type="number"
                          min="0"
                          step="any"
                          className="h-7 w-24 py-0"
                          defaultValue={String(Number(row.quantity))}
                          onBlur={(event) => {
                            const quantity = Number(event.target.value);
                            if (!(quantity > 0) || quantity === Number(row.quantity)) return;
                            save.mutate({ row, quantity, toBox: row.box_number });
                          }}
                        />
                      )}
                    </td>
                    <td className="w-16 px-2 py-1 text-[12px] text-ink-muted">{row.part_unit_code}</td>
                    {!readOnly && (
                      <td className="w-40 px-2 py-1">
                        <Select
                          aria-label={t("packing.move", "Move to box")}
                          className="h-7 py-0"
                          value={String(row.box_number)}
                          options={boxOptions}
                          onChange={(event) => save.mutate({ row, quantity: Number(row.quantity), toBox: Number(event.target.value) })}
                        />
                      </td>
                    )}
                    {!readOnly && (
                      <td className="w-10 px-2 py-1 text-right">
                        <Button size="sm" variant="ghost" icon={<X className="size-4" />} aria-label={t("packing.remove", "Take out of this box")} onClick={() => remove.mutate(row)} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
